import { ShellHeader } from '@/app/shell-header';
import { UnsavedGuard } from '@/components/unsaved-guard';
import { CATEGORIES, GROUPS } from '@/data/groups';
import {
  Button,
  ConfirmDialog,
  Counter,
  DataTable,
  ListPage,
  PageHeader,
  Switch,
  UnsavedDot,
  type DataTableColumn,
} from '@mgrvtechgroup/admin-ds';
import { TableIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { CellInput } from './cell-input';
import {
  cellText,
  changedRowIds,
  EDIT_FIELDS,
  hasErrors,
  isCellChanged,
  revertRows,
  saveRows,
  setCell,
  toggleVisible,
  validateRow,
  type Drafts,
  type EditField,
  type EditRow,
} from './inline-edit-model';

/** 더미 행 — 기준 데이터의 카테고리를 "항목"으로 쓴다. 값은 100 단위, 수량은 시드 없이 규칙으로 정한다. */
const INITIAL_ROWS: EditRow[] = CATEGORIES.map((category, index) => ({
  id: category.id,
  group: GROUPS.find((group) => group.id === category.groupId)?.name ?? '-',
  name: category.name,
  value: category.baseValue,
  quantity: ((index * 7) % 20) + 1,
  visible: index % 4 !== 3,
}));

const touchKey = (rowId: string, field: EditField) => `${rowId}:${field}`;

/**
 * 인라인 편집 표 패턴 — 표 셀의 입력으로 바로 고치고, 행 단위로 저장하거나 한꺼번에 저장한다.
 * - 고친 값은 초안(drafts)에만 두고, 바뀐 셀은 링으로·바뀐 행은 배경과 점으로 표시한다.
 * - 오류는 셀을 벗어난 뒤(또는 저장을 시도한 뒤)에 보여 준다. 입력 중에는 방해하지 않는다.
 * - "노출" 스위치는 저장 없이 즉시 반영되고 결과를 토스트로 알린다.
 * - 저장 안 한 변경이 있으면 페이지 이동(useBlocker)과 새로고침·탭 닫기(beforeunload)를 막고 묻는다.
 * DataTable은 셀에 어떤 노드든 렌더할 수 있어 Input·Switch를 그대로 넣었다(행 단위 하이라이트는 rowClassName).
 */
export function InlineEditPage() {
  const [rows, setRows] = useState(INITIAL_ROWS);
  const [drafts, setDrafts] = useState<Drafts>({});
  // 오류를 화면에 드러낼 셀 — 블러했거나 저장을 시도한 셀
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const changedIds = changedRowIds(drafts);
  const dirty = changedIds.length > 0;
  const invalidIds = changedIds.filter((id) => {
    const row = rows.find((item) => item.id === id);
    return row ? hasErrors(validateRow(row, drafts[id])) : false;
  });

  const touch = (keys: string[]) => {
    setTouched((prev) => new Set([...prev, ...keys]));
  };

  const edit = (row: EditRow, field: EditField, text: string) => {
    setDrafts((prev) => setCell(prev, row, field, text));
  };

  const revert = (ids: string[]) => {
    setDrafts((prev) => revertRows(prev, ids));
    setTouched(
      (prev) => new Set([...prev].filter((key) => !ids.some((id) => key.startsWith(`${id}:`)))),
    );
  };

  /** 행 저장·일괄 저장 공통 — 검증 실패 행은 남기고 오류를 드러내며, 결과를 토스트 한 번으로 알린다. */
  const save = (ids: string[]) => {
    const result = saveRows(rows, drafts, ids);
    setRows(result.rows);
    setDrafts(result.drafts);
    setTouched((prev) => {
      const next = new Set(prev);
      result.savedIds.forEach((id) => {
        EDIT_FIELDS.forEach((field) => next.delete(touchKey(id, field)));
      });
      result.failedIds.forEach((id) => {
        EDIT_FIELDS.forEach((field) => next.add(touchKey(id, field)));
      });
      return next;
    });
    if (result.failedIds.length === 0) {
      toast.success(`변경 ${String(result.savedIds.length)}건을 저장했습니다.`);
    } else if (result.savedIds.length === 0) {
      toast.error('입력값을 확인하세요.', {
        description: `오류가 있는 ${String(result.failedIds.length)}개 행은 저장하지 않았습니다.`,
      });
    } else {
      toast.warning(`${String(result.savedIds.length)}건을 저장했습니다.`, {
        description: `오류가 있는 ${String(result.failedIds.length)}개 행은 저장하지 않았습니다. 입력값을 확인하세요.`,
      });
    }
  };

  const changeVisible = (row: EditRow, visible: boolean) => {
    setRows((prev) => toggleVisible(prev, row.id, visible));
    toast.success(visible ? '노출을 켰습니다.' : '노출을 껐습니다.', { description: row.name });
  };

  const columns: DataTableColumn<EditRow>[] = [
    {
      key: 'group',
      header: '그룹',
      sortable: false,
      width: 120,
      cellClassName: 'text-muted-foreground',
      cell: (row) => row.group,
    },
    {
      key: 'name',
      header: '이름',
      sortable: false,
      cell: (row) => (
        <CellField
          row={row}
          field="name"
          drafts={drafts}
          touched={touched}
          onEdit={edit}
          onTouch={touch}
        />
      ),
    },
    {
      key: 'value',
      header: '값',
      align: 'right',
      sortable: false,
      width: 160,
      cell: (row) => (
        <CellField
          row={row}
          field="value"
          drafts={drafts}
          touched={touched}
          onEdit={edit}
          onTouch={touch}
        />
      ),
    },
    {
      key: 'quantity',
      header: '수량',
      align: 'right',
      sortable: false,
      width: 120,
      cell: (row) => (
        <CellField
          row={row}
          field="quantity"
          drafts={drafts}
          touched={touched}
          onEdit={edit}
          onTouch={touch}
        />
      ),
    },
    {
      key: 'visible',
      header: '노출',
      sortable: false,
      width: 80,
      cell: (row) => (
        <Switch
          size="sm"
          checked={row.visible}
          aria-label={`${row.name} 노출`}
          onCheckedChange={(checked) => {
            changeVisible(row, checked);
          }}
        />
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">행 작업</span>,
      sortable: false,
      stickyRight: true,
      width: 320,
      cell: (row) => {
        const draft = drafts[row.id];
        if (!draft) {
          return null;
        }
        const errors = validateRow(row, draft);
        const shownErrors = EDIT_FIELDS.filter(
          (field) => errors[field] && touched.has(touchKey(row.id, field)),
        );
        const [firstError] = shownErrors;
        // 오류 사유를 툴팁에만 두면 마우스를 올려야 보인다 — 행 끝에 첫 오류 문구를 늘 보여 준다(행 높이 40px 유지).
        const errorText = firstError
          ? `${errors[firstError] ?? ''}${shownErrors.length > 1 ? ` 외 ${String(shownErrors.length - 1)}건` : ''}`
          : null;
        return (
          <div className="flex items-center justify-end gap-1">
            <UnsavedDot />
            {errorText && (
              <span className="caption text-destructive min-w-0 truncate" title={errorText}>
                {errorText}
              </span>
            )}
            <Button
              size="xs"
              variant="outline"
              aria-label={`${row.name} 되돌리기`}
              onClick={() => {
                revert([row.id]);
              }}
            >
              되돌리기
            </Button>
            <Button
              size="xs"
              tone="brand"
              aria-label={`${row.name} 저장`}
              onClick={() => {
                save([row.id]);
              }}
            >
              저장
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <ListPage>
      <ShellHeader>
        <PageHeader
          title={
            <div className="flex items-center gap-2">
              <h1 className="title-md">인라인 편집 표</h1>
              <Counter>{rows.length}건</Counter>
            </div>
          }
          description="표 안의 입력으로 값을 바로 고치고 행별 또는 한 번에 저장하는 페이지입니다. 노출 스위치는 저장 없이 바로 반영됩니다."
        />
      </ShellHeader>

      <ListPage.Toolbar
        left={
          <p className="body text-muted-foreground flex items-center gap-2">
            {dirty ? (
              <>
                <UnsavedDot />
                저장하지 않은 변경 {changedIds.length}건
                {invalidIds.length > 0 && ` (입력 확인 필요 ${String(invalidIds.length)}건)`}
              </>
            ) : (
              '값을 고치면 행 끝에 저장·되돌리기가 나타납니다.'
            )}
          </p>
        }
        right={
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={!dirty}
              onClick={() => {
                revert(changedIds);
              }}
            >
              모두 되돌리기
            </Button>
            <Button
              size="sm"
              tone="brand"
              disabled={!dirty}
              onClick={() => {
                setBulkConfirm(true);
              }}
            >
              변경 {changedIds.length}건 저장
            </Button>
          </>
        }
      />

      <ListPage.Body className="border-t">
        <DataTable
          columns={columns}
          data={rows}
          rowKey={(row) => row.id}
          // 바뀐 행은 배경으로 구분한다(점·"변경됨" 이름과 함께 — 색에만 의존하지 않음).
          rowClassName={(row) => (drafts[row.id] ? '*:bg-primary/5' : undefined)}
          empty={{
            title: '아직 항목이 없습니다',
            description: '항목이 생기면 여기에 표시됩니다.',
            icon: TableIcon,
          }}
        />
      </ListPage.Body>

      <ConfirmDialog
        open={bulkConfirm}
        onOpenChange={setBulkConfirm}
        target={`변경한 ${String(changedIds.length)}개 행`}
        action="저장"
        consequence={
          invalidIds.length > 0
            ? `입력값에 오류가 있는 ${String(invalidIds.length)}개 행은 저장하지 않고 그대로 남깁니다.`
            : '변경 내용이 바로 반영됩니다.'
        }
        variant="default"
        onConfirm={() => {
          setBulkConfirm(false);
          save(changedIds);
        }}
      />

      {/* 저장 안 한 변경이 있으면 다른 페이지 이동·새로고침 전에 묻는다(C21). */}
      <UnsavedGuard when={dirty} />
    </ListPage>
  );
}

interface CellFieldProps {
  row: EditRow;
  field: EditField;
  drafts: Drafts;
  touched: ReadonlySet<string>;
  onEdit: (row: EditRow, field: EditField, text: string) => void;
  onTouch: (keys: string[]) => void;
}

const FIELD_LABEL: Record<EditField, string> = { name: '이름', value: '값', quantity: '수량' };

/** 한 셀의 입력 — 값·변경 여부·(드러낼) 오류를 초안에서 계산해 CellInput에 넘긴다. */
function CellField({ row, field, drafts, touched, onEdit, onTouch }: CellFieldProps) {
  const draft = drafts[row.id];
  const key = touchKey(row.id, field);
  const error = touched.has(key) ? validateRow(row, draft)[field] : undefined;
  return (
    <CellInput
      label={`${row.name} ${FIELD_LABEL[field]}`}
      value={cellText(row, draft, field)}
      changed={isCellChanged(draft, field)}
      error={error}
      numeric={field !== 'name'}
      onChange={(text) => {
        onEdit(row, field, text);
      }}
      onBlur={() => {
        onTouch([key]);
      }}
    />
  );
}
